import React from 'react';

import MemberCard, { IMemberCardConfig } from '../common/MemberCard';
import { IGroup, IGroupItems } from '../../types/customGroupingTypes';
import { formatCurrency } from '@utils/tools/formatCurrenyToInteger';
import {
    ERROR_FORMING_GROUPS_PRIORITY,
    GroupCSRLevels,
    PlAN_TYPES,
} from '@constants/individualPortal/customGrouping/customGrouping.constants';
import { useAppDispatch, useAppSelector, useModal, useExchangeConditions } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';
import useCustomGrouping from '../../hooks/useCustomGrouping';

interface IUnenrolledGroupCardProps {
    group: IGroupItems;
    planType: PlAN_TYPES;
    unenrolledGroupSelected: Record<string, IGroup>;
    setUnenrolledGroupSelected: Function;
    /** ID of the element that provides group context for screen readers */
    groupLabelId?: string;
}

export default function UnenrolledGroupCard(props: IUnenrolledGroupCardProps) {
    const { group, planType, unenrolledGroupSelected, setUnenrolledGroupSelected, groupLabelId } = props;
    
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const { processGroupWarning, isStateSubsidy3EligibleGroup } = useCustomGrouping({ dispatch });
    const { isMixedSubsidizedGroupSelectionAllowed } = useExchangeConditions();
    const { exchangeName } = useAppSelector(environmentSelector);

    const secondaryMembers: IMemberCardConfig[] = [];

    group.memberList.forEach(member => {
        if (
            (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
            (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
        )
            return;
        secondaryMembers.push({
            data: member,
            config: {
                isCheckBoxVisible: true,
                isChecked: !!member.isChecked,
                isCheckBoxDisabled: !!member?.enrollmentId,
            },
        });
    });

    const onMemberChecked =
        ({ unenrolledGroupSelected }: { unenrolledGroupSelected: Record<string, IGroup> }) =>
            ({ secondaryMembers, setMembersState, isCheck = true }) => {

                const updateGroupSelected = () => {
                    setUnenrolledGroupSelected(prev => {
                        return {
                            ...prev,
                            [group.id]: {
                                ...group,
                                memberList: [
                                    ...secondaryMembers
                                        .filter(member => member.config.isChecked)
                                        .map(member => ({
                                            ...member.data,
                                            isChecked: true,
                                        })),
                                ],
                            },
                        };
                    });
                };

                const errorsInGrouping:Record<string, boolean>[] = [];

                //We are iterating over the unenrolledGroupSelected to check if there is any error in the grouping with the current group.
                for (const grp of Object.values(unenrolledGroupSelected)) {
                    //If the group is the current group or the group has no members, we skip the iteration.
                    if (grp?.id === group?.id || !grp?.memberList?.length) continue;

                    const { showMultiGroupFailureModal, modalProps = {} } = processGroupWarning(grp, group, isMixedSubsidizedGroupSelectionAllowed);

                    if (showMultiGroupFailureModal) errorsInGrouping.push(modalProps);
                }

                //We are sorting the errors in the grouping based on the priority.
                errorsInGrouping.sort((a, b) => {
                    const aHasPriority = ERROR_FORMING_GROUPS_PRIORITY.some(key => a[key]);
                    const bHasPriority = ERROR_FORMING_GROUPS_PRIORITY.some(key => b[key]);
                    return bHasPriority - aHasPriority;
                });

                //If there are any errors in the grouping, we open the modal.
                if (errorsInGrouping.length && isCheck) {
                    const onContinue = () => {
                        setMembersState(secondaryMembers);
                        updateGroupSelected();
                    };

                    //We are opening the modal with the highest priority error.
                    openModal('failToFormMultiCustomGroup', { ...errorsInGrouping[0], onContinue });
                    return false;
                }

                updateGroupSelected();
                return true;
            };

    function getPlanDetailsHeader() {
        const headers: {
            header: string;
            headerValues?: Record<string, any>;
        }[] = [];
        if (planType === PlAN_TYPES.HEALTH) {
            if (group.groupCsr !== GroupCSRLevels.CS1) {
                headers.push({
                    header: 'customGrouping.csrMsg',
                    headerValues: { exchangeName },
                });
            }
        }

        if (group?.stateSubsidy2Eligible) {
            headers.push({
                header: 'customGrouping.stateSubsidy2Eligible',
            });
        }

        if (isStateSubsidy3EligibleGroup(group)) {
            headers.push({
                header: 'customGrouping.stateSubsidy3Eligible',
            });
        }

        if (group?.maxAptc >= 0) {
            headers.push({
                header: 'customGrouping.totalAPTC',
                headerValues: { amount: formatCurrency(group.maxAptc) },
            });
        }

        if (group?.maxStateSubsidy >= 0) {
            headers.push({
                header: 'customGrouping.totalStateSubsidy',
                headerValues: { amount: formatCurrency(group.maxStateSubsidy) },
            });
        }

        return headers;
    }

    const planDetails = {
        headers: getPlanDetailsHeader(),
        issuerName: group.issuerName,
        planName: group.planName,
        netPremium: group.netPremium || group.netPremium === 0 ? 'customGrouping.netPremium' : '',
        perimoumAmount: formatCurrency(group.netPremium),
    };

    return (
        <div className="mt-2">
            <div className="flex-container">
                <MemberCard
                    id={String(group.id)}
                    secondaryMembers={secondaryMembers}
                    planDetails={planDetails}
                    isBorderVisible
                    onMemberChecked={onMemberChecked({ unenrolledGroupSelected })}
                    groupLabelId={groupLabelId}
                />
            </div>
        </div>
    );
}
